import Cookies from 'js-cookie';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';

import {
  DEV_ERRORS,
  EU_COUNTRIES_LIST,
  PERSIST_SETTINGS,
  STORAGE_OPTIONS,
} from './constants/local';
import getCountryCode from './utils/getCountryCode';
import {
  COOKIE_SETTINGS,
  DEFAULT_COOKIE_SETTINGS,
  SETTINGS_MAP,
} from './constants';

const MAX_AGE = 180 * 24 * 60 * 60; // half a year (in seconds)

const { ANALYTICS } = COOKIE_SETTINGS;
const DEFAULT_SETTINGS_TO_CHECK = Object
  .values(COOKIE_SETTINGS)
  .map((setting) => ({ name: setting }));

class CookieManager {
  constructor() {
    this.initialized = false;
    this.storageKey = null;
    this.settings = null;
    // Ref to cookie notice component
    this.noticeRef = null;
    /**
     * This settings will be applied after 'accept' phase to flush (or create)
     * the storage settings according to what was accepted
     * For example, if user declined use of cookies, we need to remove them
     * entirely from cookies or localStorage, if they were set there before.
     * @type {null | Object[]} config - describes storage keys that need to be flushed/created
     * @type {string} config[].storage - one of: ['LOCAL_STORAGE', 'COOKIE']
     * @type {string} config[].key - name of cookie/localStorage item
     * @type {any} config[].defaultValue - storage key will be created with this value (only if it was provided)
     */
    this.persistSettings = PERSIST_SETTINGS;
  }

  initialize = async ({
    storageKey,
    enabled = true,
    /**
     * define if we should set domain strictly
     */
    strictDomain = false,
    active,
    settingsToCheck = DEFAULT_SETTINGS_TO_CHECK,
    settingsCookieNames = SETTINGS_MAP,
    environment,
    nodeEnv,
  }) => {
    this.storageKey = storageKey;

    if (!storageKey) throw new Error(DEV_ERRORS.STORAGE_KEY_IS_REQUIRED);
    if (!environment) throw new Error(DEV_ERRORS.ENVIRONMENT_KEY_IS_REQUIRED);

    this.initialized = true;
    this.active = active;
    this.settingsCookieNames = settingsCookieNames;
    this.isSettingsSynced = false;
    this.enabled = enabled;
    this.environment = environment;
    this.nodeEnv = nodeEnv;
    this.strictDomain = strictDomain;
    this.settings = this.getDefaultSettings({ settingsToCheck, environment });

    if (!this.active) return;

    this.isEuro = await this.getIsEuro();
    this.handleGetIsEuroResolve({ enabled, settingsToCheck, environment });

    if (this.shouldShowToolbar()) {
      this.intervalId = setInterval(this.throttleGDPRStatus, 300);
    }
  };

  // Hide current cookie toolbar
  // in case cookie policy is accepted in another tab
  throttleGDPRStatus = () => {
    if (!this.shouldShowToolbar()) {
      if (this.noticeRef) this.noticeRef.forceUpdate();

      clearInterval(this.intervalId);
    }
  };

  handleGetIsEuroResolve = ({ enabled, settingsToCheck, environment }) => {
    this.settings = this.getSettings({
      settingsToCheck,
      environment,
    });
    this.enabled = enabled || this.isEuro;
    this.isSettingsSynced = true;

    if (!this.settings[ANALYTICS]) {
      this.removeAnalytics();
    }

    if (!this.settings[ANALYTICS]) this.removeAnalytics();
    if (this.noticeRef) this.noticeRef.forceUpdate();
  };

  getDefaultSettings = ({ settingsToCheck, environment }) => this.getSettings({
    // SPEC: If geo_ip doesn't define country system should apply settings for NOT EU countries
    isEuro: false,
    settingsToCheck,
    environment,
  });

  getSettings = ({
    isEuro = this.isEuro,
    settingsToCheck,
    environment,
  }) => settingsToCheck.reduce((settings, { name }) => {
    const isAllowed = this.getCookie(this.settingsCookieNames[name]);

    // eslint-disable-next-line no-param-reassign
    settings[name] = isUndefined(isAllowed)
      ? DEFAULT_COOKIE_SETTINGS[environment][name][isEuro ? 'EU' : 'NOT_EU'].value
      : !!Number(isAllowed);

    return settings;
  }, {});

  /**
   * Important: if we don't know exactly which country is user from, set default to 'true'
   */
  getIsEuro = async () => {
    if (!isNil(this.isEuro)) return this.isEuro;

    const country = this.getCookie('country') || await getCountryCode(this.nodeEnv);

    if (country === undefined || country === '') return true;
    if (country.length !== 2) return true;

    return EU_COUNTRIES_LIST.includes(country.toUpperCase());
  };

  groupSettings = () => {
    let itemsToRemove = [];
    let itemsToCreate = [];

    Object.keys(COOKIE_SETTINGS)
      .forEach((setting) => {
        const items = this.persistSettings[setting];

        if (
          !items
          || items.length === 0
          || !Object.prototype.hasOwnProperty.call(this.settings, setting)
        ) {
          return;
        }

        if (this.settings[setting]) {
          itemsToCreate = [...itemsToCreate, ...items];
        } else {
          itemsToRemove = [...itemsToRemove, ...items];
        }
      });

    return {
      itemsToCreate,
      itemsToRemove,
    };
  };

  /**
   * After accept/decline of cookie settings we can remove/create according keys from cookies/localStorage.
   */
  persistSettingsToStorage = () => {
    const {
      itemsToRemove,
      itemsToCreate,
    } = this.groupSettings();

    const attributes = {
      path: '/',
      domain: `.${document.domain}`,
    };

    if (itemsToRemove.length > 0) {
      itemsToRemove.forEach(({
        key,
        storage,
        canRemove,
      }) => {
        if (!canRemove) return;

        // eslint-disable-next-line no-unused-expressions
        storage === STORAGE_OPTIONS.COOKIES
          ? Cookies.remove(key, attributes)
          : localStorage.removeItem(key);
      });
    }
    if (itemsToCreate.length > 0) {
      itemsToCreate.forEach(
        ({
          key,
          storage,
          defaultValue,
          canCreate,
        }) => {
          if (!canCreate) return;

          // eslint-disable-next-line no-unused-expressions
          storage === STORAGE_OPTIONS.COOKIES
            ? Cookies.set(key, defaultValue)
            : localStorage.setItem(key, defaultValue);
        }
      );
    }

    // remove analytics:
    if (!this.settings[ANALYTICS]) {
      // timeout needed here because some GA cookies become created only after
      // first user interaction, and in that case such interaction would be users click on 'accept'
      setTimeout(this.removeGoogleAnalytics, 2000);
    }
  };

  removeGoogleAnalytics = () => {
    // disabling through 'window'
    const attributes = {
      path: '/',
      domain: `.${document.domain}`,
    };

    const gaPrefixes = ['_gat_', '_dc_gtm_'];
    const regexPrefixesPart = `(${gaPrefixes.join('|')})`;
    const regexpPattern = new RegExp(`${regexPrefixesPart}.[^;]*`, 'g');
    const matched = document.cookie.match(regexpPattern);

    if (matched) {
      const ids = matched.map((item) => {
        const [name] = item.split('=');

        Cookies.remove(name, attributes);

        return name.replace(name, '');
      });

      ids.forEach((id) => {
        window[`ga-disable-${id}`] = true;
      });
    }

    this.removeCookies([...gaPrefixes, '_ga', '_gat', '_gid']);
  };

  removePlatformAnalytics = () => {
    const platformAnalyticsKeys = [
      '_spAnalyticsId',
      '_spAnalyticsIdF',
      '_spAnalyticsIdFHash',
      '_spAnalyticsIdHash',
    ];

    return this.removeCookies(platformAnalyticsKeys);
  };

  removeAnalytics = () => {
    setTimeout(() => {
      this.removePlatformAnalytics();
      this.removeGoogleAnalytics();
    }, 2000);
  };

  // eslint-disable-next-line class-methods-use-this
  removeCookies = (list) => {
    const attributes = {
      path: '/',
      domain: `.${document.domain}`,
    };

    list.forEach((cookie) => Cookies.remove(cookie, attributes));
  };

  isAccepted = () => {
    if (!this.initialized) {
      throw new Error(DEV_ERRORS.NOT_INITIALIZED);
    }

    const cookieNames = Object.keys(this.settings)
      .map(
        (setting) => this.settingsCookieNames[setting]
      );
    const isCookieSet = (cookie) => this.getCookie(cookie) !== undefined;

    return cookieNames.every((name) => isCookieSet(name));
  };

  saveSettings = (data) => {
    this.settings = {
      ...this.settings,
      ...data,
    };
  };

  getCookie = (key) => {
    if (!this.initialized) {
      console.warn(DEV_ERRORS.NOT_INITIALIZED);
    }

    return Cookies.get(key, { domain: this.getDomain() });
  };

  setCookies = (value) => {
    if (!this.initialized) {
      throw new Error(DEV_ERRORS.NOT_INITIALIZED);
    }

    const { settings } = this;
    const cookiesToUpdate = Object.keys(settings)
      .map((key) => ({
        // eslint-disable-next-line unicorn/consistent-destructuring
        name: [this.settingsCookieNames[key]],
        value: isUndefined(value) ? Number(settings[key]) : value,
      }));

    cookiesToUpdate.forEach(({
      name,
      value,
    }) => {
      const domain = this.getDomain();

      Cookies.set(name, value, {
        expires: MAX_AGE,
        ...domain && { domain },
      });
    });
    this.persistSettingsToStorage();

    return settings;
  };

  getDomain = () => {
    const { domain } = document;

    if (this.strictDomain) return domain;

    return `${domain === 'localhost' ? '' : '.'}${domain}`;
  };

  /**
   * Returns undefined in case this cookie still not exist (not accepted) or true/false
   * @param {string} name - GDPR setting name
   */
  getGDPRSetting = (name) => {
    const value = this.getCookie(this.settingsCookieNames[name]);

    return value === undefined ? value : !!Number(value);
  };

  // show toolbar only for the users that are from EU and who don't accept the cookies yet
  shouldShowToolbar = () => this.enabled && !this.isAccepted();
}

export default new CookieManager();
